import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface DefaultedAverageRow {
    CVEJUGADOR: number;
    CVETORNEO: number;
    CVECATEGORIA: number;
    PROMEDIO_USAR: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string | null;
    NOMBRE: string | null;
    APELLIDOP: string | null;
}

function bowlerName(r: DefaultedAverageRow): string {
    return r.NOMBRECOMPLETO || `${r.NOMBRE || ''} ${r.APELLIDOP || ''}`.trim() || '—';
}

type ReportTab = 'defaulted-averages';

const AdminReports: React.FC = () => {
    const navigate = useNavigate();
    const { torneoClave } = useParams<{ torneoClave: string }>();
    const location = useLocation();
    const torneoNombre: string = (location.state as any)?.torneoNombre ?? `Tournament #${torneoClave}`;

    const [activeTab, setActiveTab] = useState<ReportTab>('defaulted-averages');

    // Defaulted Averages state
    const [rows, setRows] = useState<DefaultedAverageRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    // Auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    // Load data when tab or torneoClave changes
    useEffect(() => {
        if (!torneoClave) return;
        if (activeTab === 'defaulted-averages') {
            setLoading(true);
            setError('');
            setRows([]);
            fetch(`${BACKEND_URL}/api/reports/defaulted-averages/${torneoClave}`)
                .then(r => {
                    if (!r.ok) return r.json().then(d => Promise.reject(new Error(d.error || `HTTP ${r.status}`)));
                    return r.json();
                })
                .then(data => setRows(Array.isArray(data) ? data : []))
                .catch(e => setError(e.message))
                .finally(() => setLoading(false));
        }
    }, [activeTab, torneoClave]);

    // Group rows by CVECATEGORIA for display
    const groupedRows = rows.reduce<Record<number, DefaultedAverageRow[]>>((acc, row) => {
        if (!acc[row.CVECATEGORIA]) acc[row.CVECATEGORIA] = [];
        acc[row.CVECATEGORIA].push(row);
        return acc;
    }, {});

    const searchTerm = search.toLowerCase();
    const filteredRows = search
        ? rows.filter(r =>
            r.FICHANAL.toString().includes(search) ||
            bowlerName(r).toLowerCase().includes(searchTerm)
        )
        : rows;

    const filteredGrouped = search
        ? { all: filteredRows }
        : groupedRows;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title="Back to Dashboard"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                            <p className="text-sm text-gray-500 mt-0.5">{torneoNombre}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex gap-1 -mb-px">
                        <button
                            onClick={() => setActiveTab('defaulted-averages')}
                            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                activeTab === 'defaulted-averages'
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Defaulted Averages
                        </button>
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'defaulted-averages' && (
                    <div>
                        <div className="mb-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Defaulted Averages</h2>
                            <p className="text-gray-500 text-sm">
                                Bowlers whose registered average equals the maximum allowed for their division.
                            </p>
                        </div>

                        {/* Search */}
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Search by TAT# or name..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center py-16">
                                <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                <span className="ml-3 text-gray-600">Loading report...</span>
                            </div>
                        )}

                        {error && !loading && (
                            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                                {error}
                            </div>
                        )}

                        {!loading && !error && rows.length === 0 && (
                            <div className="text-center py-16 text-gray-400">
                                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-base font-medium">No defaulted averages found for this tournament.</p>
                            </div>
                        )}

                        {!loading && !error && rows.length > 0 && (
                            <div>
                                {/* Summary badge */}
                                <div className="mb-4 flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                                        {filteredRows.length} bowler{filteredRows.length !== 1 ? 's' : ''}
                                    </span>
                                    {search && (
                                        <span className="text-xs text-gray-500">
                                            (filtered from {rows.length} total)
                                        </span>
                                    )}
                                </div>

                                {search ? (
                                    /* Flat table when searching */
                                    <div className="bg-white rounded-xl shadow overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase tracking-wide text-xs">TAT#</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase tracking-wide text-xs">Name</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase tracking-wide text-xs">Division</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-600 uppercase tracking-wide text-xs">Max Average</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {filteredRows.map(r => (
                                                    <tr key={r.CVEJUGADOR} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-mono text-gray-800 font-semibold">{r.FICHANAL}</td>
                                                        <td className="px-4 py-3 text-gray-900">{bowlerName(r)}</td>
                                                        <td className="px-4 py-3 text-gray-600">Div {r.CVECATEGORIA}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="inline-flex items-center bg-orange-100 text-orange-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                                                                {r.PROMEDIO_USAR}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    /* Grouped by division when not searching */
                                    <div className="space-y-6">
                                        {Object.entries(filteredGrouped)
                                            .sort(([a], [b]) => Number(a) - Number(b))
                                            .map(([catKey, catRows]) => {
                                                const maxAvg = catRows[0]?.PROMEDIO_USAR;
                                                return (
                                                    <div key={catKey} className="bg-white rounded-xl shadow overflow-hidden">
                                                        <div className="bg-gray-50 border-b px-4 py-3 flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <span className="font-bold text-gray-800 text-sm">Division {catKey}</span>
                                                                <span className="inline-flex items-center bg-orange-100 text-orange-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                                                                    Max avg: {maxAvg}
                                                                </span>
                                                            </div>
                                                            <span className="text-xs text-gray-500 font-medium">
                                                                {catRows.length} bowler{catRows.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <table className="min-w-full divide-y divide-gray-100 text-sm">
                                                            <thead className="bg-white">
                                                                <tr>
                                                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wide text-xs">TAT#</th>
                                                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wide text-xs">Name</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-50">
                                                                {catRows.map(r => (
                                                                    <tr key={r.CVEJUGADOR} className="hover:bg-gray-50">
                                                                        <td className="px-4 py-3 font-mono text-gray-800 font-semibold">{r.FICHANAL}</td>
                                                                        <td className="px-4 py-3 text-gray-900">{bowlerName(r)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminReports;
